import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "../../assets/style/BranchForm.css";
import {
  CustomSelect,
  getData,
  postData,
} from "../Commonfunction/CommonFunction";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  county_name: Yup.string().required("County name is required"),
  city_name: Yup.string().required("City name is required"),
  state_id: Yup.string().required("Please select state"),
  code: Yup.string().required("Code is required"),
  rate: Yup.string().required("Rate is required"),
  zip_code: Yup.string().required("Zipcode is required"),
});

const TaxTableForm = ({ onClose, id, fetchData }) => {
  const [singleData, setSingleData] = useState();
  const [stateData, setStateData] = useState();
  const navigate = useNavigate();

  const apiStateData = async () => {
    try {
      const result = await getData("/api/all-states");
      if (result.success) {
        const data = result?.data?.map((items) => ({
          label: items?.state_name,
          value: items?.id,
        }));
        setStateData(data);
      }
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  const singleCountyData = async () => {
    try {
      const result = await getData(`/api/get-county/${id}`);
      if (result.success) {
        setSingleData(result.data);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const request_url = id
        ? `/api/update-county/${id}`
        : "/api/create-county";
      const postDataResult = await postData(request_url, values);

      toast.success(postDataResult?.message);
      onClose();
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    id && singleCountyData();
  }, [id]);

  useEffect(() => {
    apiStateData();
  }, []);

  // Custom styles for form elements
  const formStyles = {
    formContainer: {
      margin: "0",
    },
    formGroup: {
      marginBottom: "12px",
    },
    label: {
      display: "block",
      fontWeight: "600",
      marginBottom: "4px",
      fontSize: "14px",
    },
    input: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      fontSize: "14px",
    },
    inputError: {
      border: "1px solid #e52207",
    },
    errorMessage: {
      color: "#e52207",
      fontSize: "12px",
      marginTop: "2px",
    },
    formFooter: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: "16px",
      gap: "8px",
    },
    button: {
      padding: "8px 16px",
      borderRadius: "4px",
      border: "none",
      cursor: "pointer",
      fontWeight: "500",
    },
    cancelButton: {
      backgroundColor: "#f0f0f0",
      color: "#1b1b1b",
    },
    submitButton: {
      backgroundColor: "#005ea2",
      color: "white",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      border: "1px solid #ddd",
    },
    tableHeader: {
      backgroundColor: "#f8f8f8",
      textAlign: "left",
      padding: "8px",
      fontSize: "14px",
      fontWeight: "600",
    },
    tableCell: {
      padding: "4px 8px",
      borderBottom: "1px solid #ddd",
    },
    tableCellInput: {
      padding: "4px 8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      width: "100%",
      textAlign: "right",
      fontSize: "14px",
    },
    tableWrapper: {
      overflowY: "auto",
      overflowX: "hidden",
      maxHeight: "250px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      marginTop: "12px",
    },
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        county_name: id ? singleData?.county_name : "",
        code: id ? singleData?.code : "",
        rate: id ? singleData?.rate : "0.0",
        state_id: id ? singleData?.state_id : "",
        city_name: id ? singleData?.city_name : "",
        applicable_date: id ? dayjs(singleData?.applicable_date) : null,
        zip_code: id ? singleData?.zip_code : "",
        vendor_comp_above_rate: id
          ? singleData?.vendor_comp_above_rate
          : "3000",
        vendor_comp_below_rate: id
          ? singleData?.vendor_comp_below_rate
          : "3000",
        vendor_comp_above_per: id ? singleData?.vendor_comp_above_per : "3.0",
        vendor_comp_below_per: id ? singleData?.vendor_comp_below_per : "0.5",
        on_road_fuel: id ? singleData?.on_road_fuel : "0.0",
        off_road_fuel: id ? singleData?.off_road_fuel : "0.0",
        special_rate: id ? singleData?.special_rate : "0.0",
        occup_tax: id ? singleData?.occup_tax : "0.0",
        occup_credit: id ? singleData?.occup_credit : "0.0",
        per_room_tax: id ? singleData?.per_room_tax : "0.0",
        food_sales: id ? singleData?.food_sales : "",
        non_food: id ? singleData?.non_food : "",
        cbd_sales: id ? singleData?.cbd_sales : "",
        local_tax: id ? singleData?.local_tax : "",
        education_tax: id ? singleData?.education_tax : "",
        local_tax_1: id ? singleData?.local_tax_1 : "",
        vendor_comp_capax_rate: id ? singleData?.vendor_comp_capax_rate : "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        handleSubmit(values);
      }}
    >
      {({ errors, touched, values, setFieldValue }) => (
        <Form style={formStyles.formContainer}>
          <div className="grid-row">
            <div className="grid-col-6">
              <div style={formStyles.formGroup}>
                <label style={formStyles.label} htmlFor="county_name">
                  County Name
                </label>
                <Field
                  id="county_name"
                  name="county_name"
                  type="text"
                  placeholder="Please enter county name"
                  style={{
                    ...formStyles.input,
                    ...(errors.county_name && touched.county_name
                      ? formStyles.inputError
                      : {}),
                  }}
                />
                <ErrorMessage
                  name="county_name"
                  component="div"
                  style={formStyles.errorMessage}
                />
              </div>
            </div>

            <div className="grid-col-6">
              <div style={formStyles.formGroup}>
                <label style={formStyles.label} htmlFor="city_name">
                  City Name
                </label>
                <Field
                  id="city_name"
                  name="city_name"
                  type="text"
                  placeholder="Please enter city name"
                  style={{
                    ...formStyles.input,
                    ...(errors.city_name && touched.city_name
                      ? formStyles.inputError
                      : {}),
                  }}
                />
                <ErrorMessage
                  name="city_name"
                  component="div"
                  style={formStyles.errorMessage}
                />
              </div>
            </div>
          </div>

          <div className="grid-row">
            <div className="grid-col-6">
              <div style={formStyles.formGroup}>
                <label style={formStyles.label} htmlFor="state_id">
                  State
                </label>
                <Field
                  name="state_id"
                  component={CustomSelect}
                  options={stateData}
                  menuPosition="none"
                />
                <ErrorMessage
                  name="state_id"
                  component="div"
                  style={formStyles.errorMessage}
                />
              </div>
            </div>
            <div className="grid-col-6">
              <div style={formStyles.formGroup}>
                <label style={formStyles.label} htmlFor="code">
                  Code
                </label>
                <Field
                  id="code"
                  name="code"
                  type="text"
                  placeholder="Enter code"
                  style={{
                    ...formStyles.input,
                    ...(errors.code && touched.code
                      ? formStyles.inputError
                      : {}),
                  }}
                />
                <ErrorMessage
                  name="code"
                  component="div"
                  style={formStyles.errorMessage}
                />
              </div>
            </div>
          </div>
          <div className="grid-row">
            <div className="grid-col-6">
              <div style={formStyles.formGroup}>
                <label style={formStyles.label} htmlFor="rate">
                  Rate
                </label>
                <Field
                  id="rate"
                  name="rate"
                  type="text"
                  placeholder="Enter Rate"
                  style={{
                    ...formStyles.input,
                    ...(errors.rate && touched.rate
                      ? formStyles.inputError
                      : {}),
                  }}
                />
                <ErrorMessage
                  name="rate"
                  component="div"
                  style={formStyles.errorMessage}
                />
              </div>
            </div>
            <div className="grid-col-6">
              <div style={formStyles.formGroup}>
                <label style={formStyles.label} htmlFor="zip_code">
                  Zipcode
                </label>
                <Field
                  id="zip_code"
                  name="zip_code"
                  type="number"
                  placeholder="Enter zipcode"
                  style={{
                    ...formStyles.input,
                    ...(errors.zip_code && touched.zip_code
                      ? formStyles.inputError
                      : {}),
                  }}
                />
                <ErrorMessage
                  name="zip_code"
                  component="div"
                  style={formStyles.errorMessage}
                />
              </div>
            </div>
          </div>

          {id && (
            <div className="grid-row">
              <div className="grid-col-12">
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label} htmlFor="applicable_date">
                    Applicable Date
                  </label>
                  <input
                    id="applicable_date"
                    name="applicable_date"
                    type="date"
                    style={{
                      ...formStyles.input,
                      ...(errors.applicable_date && touched.applicable_date
                        ? formStyles.inputError
                        : {}),
                    }}
                    value={values.applicable_date || ""}
                    onChange={(e) =>
                      setFieldValue("applicable_date", e.target.value)
                    }
                  />
                  {errors.applicable_date && touched.applicable_date && (
                    <div style={formStyles.errorMessage}>
                      {errors.applicable_date}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={formStyles.tableWrapper}>
            <table style={formStyles.table}>
              <thead>
                <tr>
                  <th style={{ ...formStyles.tableHeader, width: "200px" }}>
                    Category
                  </th>
                  <th style={{ ...formStyles.tableHeader, width: "200px" }}>
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    id: "food_sales",
                    label: "Food Sales",
                    placeholder: "Food sales",
                  },
                  {
                    id: "non_food",
                    label: "Non Food",
                    placeholder: "Non food",
                  },
                  {
                    id: "cbd_sales",
                    label: "CBD Sales",
                    placeholder: "CBD sales",
                  },
                  {
                    id: "local_tax",
                    label: "Local Tax",
                    placeholder: "Local tax",
                  },
                  {
                    id: "vendor_comp_below_rate",
                    label: "Non MF Sales Below Rate $",
                    placeholder: "Below Rate $",
                  },
                  {
                    id: "vendor_comp_below_per",
                    label: "Non MF Sales Below %",
                    placeholder: "Below %",
                  },
                  {
                    id: "vendor_comp_above_rate",
                    label: "Non MF Sales Above Rate $",
                    placeholder: "Above Rate $",
                  },
                  {
                    id: "vendor_comp_above_per",
                    label: "Non MF Sales Above %",
                    placeholder: "Above %",
                  },
                  {
                    id: "on_road_fuel",
                    label: "On Road Fuel",
                    placeholder: "On Road Fuel",
                  },
                  {
                    id: "off_road_fuel",
                    label: "Off Road Fuel",
                    placeholder: "Off Road Fuel",
                  },
                  {
                    id: "special_rate",
                    label: "Special Rate",
                    placeholder: "Special Rate",
                  },
                  {
                    id: "occup_tax",
                    label: "Occup Tax",
                    placeholder: "Occup Tax",
                  },
                  {
                    id: "occup_credit",
                    label: "Occup Credit",
                    placeholder: "Occup Credit",
                  },
                  {
                    id: "per_room_tax",
                    label: "Per Room Tax",
                    placeholder: "Per Room Tax",
                  },
                  {
                    id: "education_tax",
                    label: "Education Tax",
                    placeholder: "Education Tax",
                  },
                  {
                    id: "local_tax_1",
                    label: "Local Tax 1",
                    placeholder: "Local Tax 1",
                  },
                  {
                    id: "vendor_comp_capax_rate",
                    label: "Maximum Discount",
                    placeholder: "Maximum Discount",
                  },
                ].map(({ id, label, placeholder }) => (
                  <tr key={id}>
                    <td style={{ ...formStyles.tableCell, fontWeight: 600 }}>
                      {label}
                    </td>
                    <td
                      style={{
                        ...formStyles.tableCell,
                        background: "#ffffff",
                        padding: 0,
                      }}
                    >
                      <Field
                        id={id}
                        name={id}
                        type="number"
                        step="0.01"
                        placeholder={placeholder}
                        style={formStyles.tableCellInput}
                      />
                      {errors[id] && touched[id] && (
                        <div style={formStyles.errorMessage}>{errors[id]}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-footer">
            <button type="button" className="btn btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-submit">
              Submit
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TaxTableForm;
